import { useState, useRef } from 'react';
import {
  ArrowLeft,
  Building2,
  Receipt,
  CreditCard,
  ShoppingCart,
  FileText,
  User,
  Settings2,
  Loader2,
  MapPin,
  ChevronDown,
  ChevronRight,
  Palette,
  Upload,
  ImageIcon,
  CheckCircle2,
  Key,
  Eye,
  EyeOff,
  Heart,
  Sparkles,
  Store,
  Trash2,
  Boxes,
  Landmark,
  Tag,
  Banknote,
  Pencil,
  Plus,
  Star,
  BarChart3,
} from 'lucide-react';
import { toast } from '@/stores/toast-store';
import { apiErrorMessage } from '@/lib/api-error';
import { StatusBadge } from '@/components/shared/status-badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ImageCropModal } from '@/components/ui/image-crop-modal';
import {
  useAdminTenantDetail,
  useActivateTenant,
  useDeactivateTenant,
  useToggleTenantModule,
  useActivateLocation,
  useUpdateAdminLocation,
  useUpdateTenantFeatureFlags,
  useLocationPerLocationCount,
  useWipeLocationProducts,
} from '@/hooks/use-admin';
import { COMMON_TIME_ZONES, DEFAULT_TIME_ZONE, formatTimeZoneLabel } from '@/lib/report-timezone';
import { useRpPaymentGatewaysConfig, useSaveRpPaymentGatewayConfig } from '@/hooks/use-rp-payment-gateways-config';
import {
  useAdminFundingAccounts,
  useAdminSetPrimaryFundingAccount,
  type AdminFundingAccount,
  type AdminFundingSide,
} from '@/hooks/use-admin-funding-accounts';
import { AdminFundingAccountModal } from './admin-funding-account-modal';
import {
  useProductsPosConfig,
  useUpdateCustomerEngagementEnabled,
  useUpdatePromotionsEnabled,
  useUpdateBrandColorsEnabled,
  useUpdateCampaignSmsNumbers,
} from '@/hooks/use-pos-config';
import {
  useBusinessBranding,
  useSaveBusinessBranding,
  useUploadLogo,
  useUploadFavicon,
} from '@/hooks/use-business-branding';
import { cn, formatDateTime } from '@/lib/utils';
import { getStateName } from '@/lib/constants';
import { formatPhone } from '@/lib/validators';
import { LocationTypeIcon } from '@/components/layout/header';
import { ReceivePaymentMethodSwitches } from '@/components/admin/receive-payment-method-switches';
import { PaymentsApiRoutingSwitch } from '@/components/admin/payments-api-routing-switch';
import { useCampaignApiPlatformEnabled, useEslPlatformEnabled } from '@/hooks/use-feature-flags';
import { EslConnectionCard } from '@/components/admin/esl-connection-card';
import { EslApiCredentials } from '@/components/admin/esl-api-credentials';
import { CampaignApiCredentialsRow } from '@/components/admin/campaign-api-credentials';
import { PrivateWarehouseMappingsCard } from '@/components/admin/private-warehouse-mappings';
import { isWarehouseType, locationTypeLabel } from '@/lib/location-type';

const MODULE_INFO: Record<string, { name: string; description: string; icon: React.ElementType }> = {
  collections: { name: 'Receive Payments', description: 'Invoice and payment collection', icon: Receipt },
  payables: { name: 'Send Payments', description: 'Bill and vendor payment management', icon: CreditCard },
  products: {
    name: 'POS Management',
    description: 'Point-of-sale, products, departments, inventory',
    icon: ShoppingCart,
  },
  online_store: {
    name: 'Online Store',
    description: 'WooCommerce online store sync (per-location)',
    icon: Store,
  },
  warehouse_packing: {
    name: 'Warehouse Packing',
    description: 'Consolidated PO packing list for the warehouse team',
    icon: Boxes,
  },
  reconciliation: {
    name: 'Reconciliation',
    description: 'Revel/CC-Funding and SuperNova reconciliation reports',
    icon: Landmark,
  },
  statements: {
    name: 'Statements',
    description: 'ACH, Card and Campaign Subscription Fee statement PDFs',
    icon: FileText,
  },
  esl: {
    name: 'Shelf Labels',
    description: 'Electronic shelf labels — Supernova POS, external POS and standalone integrations',
    icon: Tag,
  },
  funding_transactions: {
    name: 'Transactions',
    description: 'Standalone view of processor funding-file transactions (cc_funding_target_rows)',
    icon: Banknote,
  },
  reporting: {
    name: 'Reports',
    description: 'Reports page — POS, Receive Payments and Send Payments reports',
    icon: BarChart3,
  },
};

// Modules listed here follow the gate → merchant-toggle flow: the super admin
// opens the gate, then the business admin flips `isActive` on their side.
// Invoice Processor is intentionally NOT in this list — it has no merchant-side
// toggle, so it's rendered as its own row next to Customer Engagement.
const SUPER_ADMIN_MODULES = ['collections', 'payables', 'products', 'online_store', 'esl'] as const;

// Modules whose existence is decided by a PLATFORM release switch in Global
// Settings, not by this page. Without that check a super admin could open a
// per-tenant gate for a feature the API still refuses, which looks like a bug in
// the gate rather than a switch that is off.
const PLATFORM_GATED_MODULES: Record<string, 'esl'> = { esl: 'esl' };

// A shelf-labels-only business is not a payments merchant and has no till, so
// the payments and POS modules are not things a super admin can grant it —
// they are not on its menu at all. Mirrors offerableModules() in the API, which
// is what actually enforces this; showing a toggle the server would reject
// makes the super admin's screen lie about what is possible.
const ESL_TENANT_MODULES: readonly string[] = ['esl'];

function modulesForTenantKind(
  list: readonly string[],
  tenantType: string | undefined,
  platform: { esl: boolean },
): string[] {
  const afterPlatformSwitch = list.filter((m) => {
    const flag = PLATFORM_GATED_MODULES[m];
    return !flag || platform[flag];
  });
  if (tenantType === 'esl') return afterPlatformSwitch.filter((m) => ESL_TENANT_MODULES.includes(m));
  // The v0.1 "messaging partner" kind is retired (Campaign API credentials now
  // belong to a store's location), and it was never offered anything else.
  if (tenantType === 'companion') return [];
  return afterPlatformSwitch;
}

// Single-phase modules: the super admin gate is the only switch — there's no
// merchant-side isActive step (mirrors warehouse_packing, which is appended
// separately below only for warehouse locations). Kept out of
// SUPER_ADMIN_MODULES so its two-phase "business hasn't turned it on yet"
// copy isn't misapplied to a module that has no such step.
const SINGLE_PHASE_MODULES = ['reconciliation', 'statements', 'funding_transactions', 'reporting'] as const;

// Modules that are ON until the super admin turns them off: with no row saved,
// the API reads them as open (they're outside its SUPER_ADMIN_GATED_MODULES), so
// every existing business keeps them. The toggles here must show that same
// effective state rather than treating "no row" as off.
const DEFAULT_ON_MODULES: readonly string[] = ['reporting'];

// Master-gated modules: the tenant-wide switch is a prerequisite, not a
// default. Each location's switch then decides availability there (a
// location with no row of its own inherits ON). Mirrors MASTER_GATED_MODULES
// in the API's modules route, which ANDs the two at read time.
const MASTER_GATED_MODULES = ['statements'] as const;

interface AdminTenantDetailPageProps {
  tenantId: string;
}

export function AdminTenantDetailPage({ tenantId }: AdminTenantDetailPageProps) {
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const eslPlatformEnabled = useEslPlatformEnabled();
  const campaignApiPlatformEnabled = useCampaignApiPlatformEnabled();
  const [expandedLocation, setExpandedLocation] = useState<string | null>(null);
  const { data, isLoading, isError, error, refetch } = useAdminTenantDetail(tenantId);
  const activateTenant = useActivateTenant();
  const deactivateTenant = useDeactivateTenant();
  const toggleModule = useToggleTenantModule();
  const activateLocation = useActivateLocation();
  const updateAdminLocation = useUpdateAdminLocation();
  const updateFeatureFlags = useUpdateTenantFeatureFlags();

  const tenant = data?.data;

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>;
  }

  // Only a real 404 is "not found". Anything else (500, network, auth) is a
  // failed load — say so, and offer a retry, rather than implying the tenant
  // doesn't exist.
  if (isError && (error as { error?: { code?: string } })?.error?.code !== 'NOT_FOUND') {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3 text-center">
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Couldn't load this tenant: {apiErrorMessage(error, 'request failed')}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Try again
          </button>
          <a href="#/admin" className="px-2 py-2 text-sm text-muted-foreground hover:text-foreground">
            Back to Businesses
          </a>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 text-center">
        <div className="text-muted-foreground">Tenant not found</div>
        <a href="#/admin" className="text-sm text-muted-foreground underline hover:text-foreground">
          Back to Businesses
        </a>
      </div>
    );
  }

  // Depends on `tenant`, so it has to sit after the not-found guard.
  const tenantKind = (tenant as { type?: string }).type;
  // Shelf-labels-only and messaging-partner accounts are not payments merchants
  // and have no till, so the per-store POS rows below don't apply to them.
  const isEslOnly = tenantKind === 'esl' || tenantKind === 'companion';
  const platform = { esl: eslPlatformEnabled };
  const superAdminModules = modulesForTenantKind(SUPER_ADMIN_MODULES, tenantKind, platform);
  const singlePhaseModules = modulesForTenantKind(SINGLE_PHASE_MODULES, tenantKind, platform);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <a href="#/admin" className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground">
          <ArrowLeft className="h-5 w-5" />
        </a>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-foreground">{tenant.name}</h1>
          <p className="text-sm text-muted-foreground">Business ID: {tenant.slug}</p>
        </div>
        {tenant.isActive ? (
          <button
            onClick={() => setShowDeactivateConfirm(true)}
            disabled={deactivateTenant.isPending}
            className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
          >
            Deactivate Business
          </button>
        ) : (
          <button
            onClick={() => activateTenant.mutate(tenantId)}
            disabled={activateTenant.isPending}
            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
          >
            Activate Business
          </button>
        )}
      </div>

      {/* Business Info Card */}
      <div className="rounded-xl border border-border bg-white p-6">
        <div className="mb-4 flex items-center gap-3">
          <Building2 className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Business Information</h2>
          <StatusBadge status={tenant.isActive ? 'active' : 'inactive'} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <p className="text-xs text-muted-foreground">Owner</p>
            <p className="text-sm text-foreground">{tenant.owner?.name || 'N/A'}</p>
            <p className="text-xs text-muted-foreground">{tenant.owner?.email || ''}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Phone</p>
            <p className="text-sm text-foreground">{formatPhone(tenant.phone) || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Address</p>
            <p className="text-sm text-foreground">
              {[tenant.street, tenant.city, getStateName(tenant.state), tenant.zip].filter(Boolean).join(', ') || 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Country</p>
            <p className="text-sm text-foreground">{tenant.country || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Created</p>
            <p className="text-sm text-foreground">{formatDateTime(tenant.createdAt)}</p>
          </div>
        </div>
      </div>

      {/* Feature Flags */}
      <div className="rounded-xl border border-border bg-white p-6">
        <div className="mb-4 flex items-center gap-3">
          <Settings2 className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Feature Flags</h2>
        </div>
        <div className="space-y-3">
          {/* Add Company gate. Defaults to disabled — anything other than the
              explicit value `true` is treated as off, matching the DB default. */}
          {(() => {
            const addCompanyEnabled = tenant.canAddLocations === true;
            return (
              <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">Add Company</div>
                  <p className="text-xs text-muted-foreground">
                    When disabled, the business cannot create new locations — the &quot;Add Company&quot; entry is
                    hidden from the location picker. Existing locations remain accessible.
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {addCompanyEnabled ? (
                      <span className="text-green-700">Enabled · business can add new locations</span>
                    ) : (
                      <span className="text-amber-700">Disabled · business cannot add new locations</span>
                    )}
                  </p>
                </div>
                <button
                  title={
                    addCompanyEnabled
                      ? 'Block this business from adding new locations'
                      : 'Allow this business to add new locations'
                  }
                  onClick={() =>
                    updateFeatureFlags.mutate({
                      tenantId,
                      canAddLocations: !addCompanyEnabled,
                    })
                  }
                  disabled={updateFeatureFlags.isPending}
                  className={cn(
                    'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
                    addCompanyEnabled ? 'bg-primary' : 'bg-gray-200',
                  )}
                >
                  <span
                    className={cn(
                      'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                      addCompanyEnabled ? 'translate-x-4' : 'translate-x-0.5',
                    )}
                  />
                </button>
              </div>
            );
          })()}

          {/* Generic Product Images gate. When ON, POS devices for this tenant
              will receive common product images via sync and display them as
              fallback when a product has no dedicated image. */}
          {(() => {
            const genericImagesEnabled = tenant.useGenericProductImages === true;
            return (
              <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">Generic Product Images</div>
                  <p className="text-xs text-muted-foreground">
                    When enabled, POS devices will use common product images as fallback for products without dedicated
                    images. Images are matched by keyword against product names.
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {genericImagesEnabled ? (
                      <span className="text-green-700">Enabled · common images synced to POS</span>
                    ) : (
                      <span className="text-amber-700">Disabled · POS shows themed avatars only</span>
                    )}
                  </p>
                </div>
                <button
                  title={
                    genericImagesEnabled
                      ? 'Stop syncing generic product images to POS'
                      : 'Enable generic product images for POS'
                  }
                  onClick={() =>
                    updateFeatureFlags.mutate({
                      tenantId,
                      useGenericProductImages: !genericImagesEnabled,
                    })
                  }
                  disabled={updateFeatureFlags.isPending}
                  className={cn(
                    'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
                    genericImagesEnabled ? 'bg-primary' : 'bg-gray-200',
                  )}
                >
                  <span
                    className={cn(
                      'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                      genericImagesEnabled ? 'translate-x-4' : 'translate-x-0.5',
                    )}
                  />
                </button>
              </div>
            );
          })()}

          {/* Variable-price barcode gate. When ON, the tenant's POS Config page
              shows the barcode-pattern dropdown so they can pick their layout;
              when OFF the whole feature is hidden from them. */}
          {(() => {
            const variableBarcodeEnabled = tenant.variableBarcodeEnabled === true;
            return (
              <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">Variable-price Barcodes</div>
                  <p className="text-xs text-muted-foreground">
                    When enabled, this tenant can choose a variable-price (&ldquo;embedded price&rdquo;) barcode format
                    on their POS Config page. The POS then rings items at the price embedded in the barcode.
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {variableBarcodeEnabled ? (
                      <span className="text-green-700">Enabled · tenant can select a barcode format</span>
                    ) : (
                      <span className="text-amber-700">Disabled · hidden from POS Config</span>
                    )}
                  </p>
                </div>
                <button
                  title={
                    variableBarcodeEnabled
                      ? 'Hide variable-price barcode settings from this tenant'
                      : 'Let this tenant configure variable-price barcodes'
                  }
                  onClick={() =>
                    updateFeatureFlags.mutate({
                      tenantId,
                      variableBarcodeEnabled: !variableBarcodeEnabled,
                    })
                  }
                  disabled={updateFeatureFlags.isPending}
                  className={cn(
                    'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
                    variableBarcodeEnabled ? 'bg-primary' : 'bg-gray-200',
                  )}
                >
                  <span
                    className={cn(
                      'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                      variableBarcodeEnabled ? 'translate-x-4' : 'translate-x-0.5',
                    )}
                  />
                </button>
              </div>
            );
          })()}

          {/* Auto-generated barcode gate. When ON, the tenant's POS Config page
              shows the Barcodes card where they set a starting barcode and an
              increment; the products service enforces the same flag before it
              will allocate. Turning it OFF also stops registers offering
              "Generate" on the next config sync. */}
          {(() => {
            const barcodeAutogenEnabled = tenant.barcodeAutogenEnabled === true;
            return (
              <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">Auto-generate Barcodes</div>
                  <p className="text-xs text-muted-foreground">
                    When enabled, this tenant can set a starting barcode and an increment on their POS Config page, and
                    staff can then issue a new barcode from the Dashboard, POS or mobile app instead of inventing one.
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {barcodeAutogenEnabled ? (
                      <span className="text-green-700">Enabled · tenant can configure their barcode series</span>
                    ) : (
                      <span className="text-amber-700">Disabled · hidden from POS Config</span>
                    )}
                  </p>
                </div>
                <button
                  title={
                    barcodeAutogenEnabled
                      ? 'Hide barcode generation from this tenant'
                      : 'Let this tenant generate their own barcodes'
                  }
                  onClick={() =>
                    updateFeatureFlags.mutate({
                      tenantId,
                      barcodeAutogenEnabled: !barcodeAutogenEnabled,
                    })
                  }
                  disabled={updateFeatureFlags.isPending}
                  className={cn(
                    'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
                    barcodeAutogenEnabled ? 'bg-primary' : 'bg-gray-200',
                  )}
                >
                  <span
                    className={cn(
                      'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                      barcodeAutogenEnabled ? 'translate-x-4' : 'translate-x-0.5',
                    )}
                  />
                </button>
              </div>
            );
          })()}

          {(() => {
            const productExportEnabled = tenant.productExportEnabled === true;
            return (
              <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">Product / Inventory Export</div>
                  <p className="text-xs text-muted-foreground">
                    When enabled, this tenant can export their full product &amp; inventory catalog to a spreadsheet
                    from the Products page. Keep it OFF to protect the catalog from being bulk-exported to migrate away
                    — the export is blocked both in the dashboard and on the server.
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {productExportEnabled ? (
                      <span className="text-green-700">Enabled · tenant can export products &amp; inventory</span>
                    ) : (
                      <span className="text-amber-700">Disabled · export hidden and blocked server-side</span>
                    )}
                  </p>
                </div>
                <button
                  title={
                    productExportEnabled
                      ? 'Block this tenant from exporting products/inventory'
                      : 'Let this tenant export their products/inventory'
                  }
                  onClick={() =>
                    updateFeatureFlags.mutate({
                      tenantId,
                      productExportEnabled: !productExportEnabled,
                    })
                  }
                  disabled={updateFeatureFlags.isPending}
                  className={cn(
                    'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
                    productExportEnabled ? 'bg-primary' : 'bg-gray-200',
                  )}
                >
                  <span
                    className={cn(
                      'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                      productExportEnabled ? 'translate-x-4' : 'translate-x-0.5',
                    )}
                  />
                </button>
              </div>
            );
          })()}

          {/* Tenant Product Image Upload gate. When ON, the business can upload
              its own product image on the POS Management → product details page;
              the products service enforces this same flag on its upload endpoint.
              A tenant image takes priority over the generic image on POS. */}
          {(() => {
            const uploadEnabled = tenant.allowTenantProductImageUpload === true;
            return (
              <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">Tenant Product Image Upload</div>
                  <p className="text-xs text-muted-foreground">
                    When enabled, the business can upload its own product image from the product details page. Uploaded
                    images sync to POS and take priority over generic images.
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {uploadEnabled ? (
                      <span className="text-green-700">Enabled · business can upload product images</span>
                    ) : (
                      <span className="text-amber-700">Disabled · business cannot upload product images</span>
                    )}
                  </p>
                </div>
                <button
                  title={
                    uploadEnabled
                      ? 'Block this business from uploading product images'
                      : 'Allow this business to upload product images'
                  }
                  onClick={() =>
                    updateFeatureFlags.mutate({
                      tenantId,
                      allowTenantProductImageUpload: !uploadEnabled,
                    })
                  }
                  disabled={updateFeatureFlags.isPending}
                  className={cn(
                    'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
                    uploadEnabled ? 'bg-primary' : 'bg-gray-200',
                  )}
                >
                  <span
                    className={cn(
                      'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                      uploadEnabled ? 'translate-x-4' : 'translate-x-0.5',
                    )}
                  />
                </button>
              </div>
            );
          })()}

          {/* Category & Department Image Upload gate. When ON, the business can
              upload a tile image for each category and department; the products
              service enforces this same flag on its /categories/:id/image and
              /departments/:id/image endpoints, and the images sync to the POS
              category/department tiles. */}
          {(() => {
            const catDeptEnabled = tenant.allowCategoryDepartmentImageUpload === true;
            return (
              <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">Category &amp; Department Image Upload</div>
                  <p className="text-xs text-muted-foreground">
                    When enabled, the business can upload a tile image for each category and department. Uploaded images
                    sync to the POS and show on the Category and Departments tiles.
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {catDeptEnabled ? (
                      <span className="text-green-700">Enabled · business can upload category/department images</span>
                    ) : (
                      <span className="text-amber-700">
                        Disabled · business cannot upload category/department images
                      </span>
                    )}
                  </p>
                </div>
                <button
                  title={
                    catDeptEnabled
                      ? 'Block this business from uploading category/department images'
                      : 'Allow this business to upload category/department images'
                  }
                  onClick={() =>
                    updateFeatureFlags.mutate({
                      tenantId,
                      allowCategoryDepartmentImageUpload: !catDeptEnabled,
                    })
                  }
                  disabled={updateFeatureFlags.isPending}
                  className={cn(
                    'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
                    catDeptEnabled ? 'bg-primary' : 'bg-gray-200',
                  )}
                >
                  <span
                    className={cn(
                      'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                      catDeptEnabled ? 'translate-x-4' : 'translate-x-0.5',
                    )}
                  />
                </button>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Tenant-wide module gates */}
      <div className="rounded-xl border border-border bg-white p-6">
        <div className="mb-4 flex items-center gap-3">
          <Settings2 className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Modules</h2>
          <span className="text-sm text-muted-foreground">tenant-wide</span>
        </div>
        <p className="mb-4 text-[11px] text-muted-foreground">
          Toggling here grants/revokes the module across every location for this business. Future locations will inherit
          this setting automatically. Use the per-location toggles below to override for a specific location.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {[...superAdminModules, ...singlePhaseModules].map((name) => {
            const info = MODULE_INFO[name];
            if (!info) return null;
            const Icon = info.icon;
            const tenantWideRow = (tenant.modules as Array<any> | undefined)?.find(
              (m: any) => m.moduleName === name && !m.locationId,
            );
            const gateOpen = tenantWideRow
              ? tenantWideRow.superAdminEnabled === true
              : DEFAULT_ON_MODULES.includes(name);
            return (
              <div
                key={`tenant-wide-${name}`}
                className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <Icon className="h-4 w-4 text-primary" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{info.name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {(MASTER_GATED_MODULES as readonly string[]).includes(name) ? (
                        // Master switch: turning it on doesn't grant every location,
                        // it lets each location's own switch below take effect.
                        gateOpen ? (
                          <span className="text-green-600">On for the business · set per location below</span>
                        ) : (
                          <span>Off for the business · location switches below are inactive</span>
                        )
                      ) : gateOpen ? (
                        <span className="text-green-600">Granted across all locations</span>
                      ) : (
                        <span>Not granted at the tenant level</span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  title={
                    gateOpen
                      ? `Revoke ${info.name} for the whole business`
                      : `Grant ${info.name} across every location for this business`
                  }
                  onClick={() =>
                    toggleModule.mutate({
                      tenantId,
                      moduleName: name,
                      isActive: !gateOpen,
                      // No locationId → backend treats this as a tenant-wide gate
                      // and fans out to every existing per-location row.
                    })
                  }
                  disabled={toggleModule.isPending}
                  className={cn(
                    'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
                    gateOpen ? 'bg-primary' : 'bg-gray-200',
                  )}
                >
                  <span
                    className={cn(
                      'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                      gateOpen ? 'translate-x-4' : 'translate-x-0.5',
                    )}
                  />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Shelf Labels connection. Super-admin only, and only once the platform
          switch is on and this business has actually been granted the module —
          configuring a vendor account for a tenant that cannot use it would be
          storing a credential for nothing. */}
      {eslPlatformEnabled &&
        (tenant.modules as Array<any> | undefined)?.some(
          (m: any) => m.moduleName === 'esl' && !m.locationId && m.superAdminEnabled,
        ) && (
          <>
            <EslConnectionCard
              tenantId={tenantId}
              locations={(tenant.locations ?? []).map((l: any) => ({ id: l.id, name: l.name, address: l.address }))}
            />
            <EslApiCredentials
              tenantId={tenantId}
              locations={(tenant.locations ?? []).map((l: any) => ({ id: l.id, name: l.name }))}
            />
          </>
        )}

      {/* Locations */}
      {tenant.locations && tenant.locations.length > 0 && (
        <div className="rounded-xl border border-border bg-white p-6">
          <div className="mb-4 flex items-center gap-3">
            <MapPin className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold text-foreground">Locations</h2>
            <span className="text-sm text-muted-foreground">({tenant.locations.length})</span>
          </div>
          <div className="space-y-3">
            {tenant.locations.map((loc: any) => {
              const isExpanded = expandedLocation === loc.id;
              // Warehouse Packing only makes sense at warehouse locations, so
              // the toggle is only offered there. Unlike the other modules it
              // has no merchant-side isActive step: opening the gate is what
              // makes the Packing tab appear for the business.
              const moduleNames = isWarehouseType(loc.type)
                ? [...superAdminModules, 'warehouse_packing', ...singlePhaseModules]
                : [...superAdminModules, ...singlePhaseModules];
              const locationModules = moduleNames.map((name) => {
                const existing = loc.modules?.find((m: any) => m.moduleName === name);
                const masterGated = (MASTER_GATED_MODULES as readonly string[]).includes(name);
                const tenantWideRow = (tenant.modules as Array<any> | undefined)?.find(
                  (m: any) => m.moduleName === name && !m.locationId,
                );
                const masterOpen = tenantWideRow?.superAdminEnabled === true;
                // Default-on modules inherit like GET /modules resolves them:
                // this location's row, else the tenant-wide row, else ON.
                const defaultOnGate = existing
                  ? existing.superAdminEnabled === true
                  : tenantWideRow
                    ? tenantWideRow.superAdminEnabled === true
                    : true;
                return {
                  moduleName: name,
                  // Super admin toggle reflects the gate, not the business-side
                  // activation. The gate controls whether the business admin
                  // is allowed to flip their own toggle on. Master-gated
                  // modules show the *effective* state — tenant-wide AND this
                  // location's own switch (inheriting ON without a row).
                  superAdminEnabled: masterGated
                    ? masterOpen && (existing ? existing.superAdminEnabled === true : true)
                    : DEFAULT_ON_MODULES.includes(name)
                      ? defaultOnGate
                      : existing?.superAdminEnabled || false,
                  masterGated,
                  masterOpen,
                  isActive: existing?.isActive || false,
                  activatedAt: existing?.activatedAt || null,
                };
              });

              return (
                <div key={loc.id} className="rounded-lg border border-border">
                  {/* Location Header */}
                  <button
                    onClick={() => setExpandedLocation(isExpanded ? null : loc.id)}
                    className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <LocationTypeIcon type={loc.type} className="h-4 w-4" />
                      <div>
                        <span className="font-medium text-foreground">{loc.name}</span>
                        {loc.address && <p className="text-xs text-muted-foreground">{loc.address}</p>}
                      </div>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                          loc.type === 'private_warehouse'
                            ? 'bg-violet-50 text-violet-700'
                            : isWarehouseType(loc.type)
                              ? 'bg-orange-50 text-orange-700'
                              : 'bg-slate-100 text-slate-700',
                        )}
                      >
                        {locationTypeLabel(loc.type)}
                      </span>
                      <StatusBadge status={loc.isActive ? 'active' : 'inactive'} />
                      {loc.onboarding?.isSubmitted && (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                          Registered
                        </span>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>

                  {/* Expanded Location Details */}
                  {isExpanded && (
                    <div className="border-t border-border p-4 space-y-4">
                      {/* Activate / Deactivate Location */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Location Status</span>
                        <button
                          onClick={() =>
                            activateLocation.mutate({
                              tenantId,
                              locationId: loc.id,
                              isActive: !loc.isActive,
                            })
                          }
                          disabled={activateLocation.isPending}
                          className={cn(
                            'rounded-lg px-3 py-1.5 text-xs font-medium',
                            loc.isActive
                              ? 'border border-red-200 text-red-600 hover:bg-red-50'
                              : 'bg-green-600 text-white hover:bg-green-700',
                          )}
                        >
                          {loc.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>

                      {/* Timezone — single source of truth for this location's
                          date/time display, selection and day-boundary math. */}
                      {/* Contact email — Reply-To / buyer email on purchase orders
                          sent for this location (never the platform mailbox). */}
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <span className="text-sm text-muted-foreground">Contact Email</span>
                          <p className="mt-0.5 text-[11px] text-muted-foreground">
                            Vendors reply to this address on purchase orders. Blank falls back to the registration
                            email.
                          </p>
                        </div>
                        <LocationContactEmailField
                          key={`${loc.id}:${loc.email ?? ''}`}
                          value={loc.email ?? ''}
                          disabled={updateAdminLocation.isPending}
                          onSave={(email) =>
                            updateAdminLocation.mutate(
                              { tenantId, locationId: loc.id, email },
                              {
                                onSuccess: () => toast.success(email ? 'Contact email saved' : 'Contact email cleared'),
                                onError: (err: any) =>
                                  toast.error(err?.error?.message || 'Failed to save contact email'),
                              },
                            )
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <span className="text-sm text-muted-foreground">Timezone</span>
                          <p className="mt-0.5 text-[11px] text-muted-foreground">
                            Applied across the app for this location's dates &amp; times.
                          </p>
                        </div>
                        <select
                          value={loc.timezone || DEFAULT_TIME_ZONE}
                          disabled={updateAdminLocation.isPending}
                          onChange={(e) =>
                            updateAdminLocation.mutate({
                              tenantId,
                              locationId: loc.id,
                              timezone: e.target.value,
                            })
                          }
                          className="h-9 w-56 rounded-lg border border-input px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50"
                        >
                          {COMMON_TIME_ZONES.map((z) => (
                            <option key={z.value} value={z.value}>
                              {formatTimeZoneLabel(z.value)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Location type — only super admin can make a location a
                          Private Warehouse (shareable with other tenants). */}
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <span className="text-sm text-muted-foreground">Location Type</span>
                          <p className="mt-0.5 text-[11px] text-muted-foreground">
                            A Private Warehouse works like a warehouse here and can be mapped to other tenants' stores.
                          </p>
                        </div>
                        <select
                          value={loc.type || 'company'}
                          disabled={updateAdminLocation.isPending}
                          onChange={(e) =>
                            updateAdminLocation.mutate(
                              {
                                tenantId,
                                locationId: loc.id,
                                type: e.target.value as 'company' | 'warehouse' | 'private_warehouse',
                              },
                              {
                                onError: (err: any) =>
                                  toast.error(err?.error?.message || 'Failed to change location type'),
                              },
                            )
                          }
                          className="h-9 w-56 rounded-lg border border-input px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50"
                        >
                          <option value="company">Store</option>
                          <option value="warehouse">Warehouse</option>
                          <option value="private_warehouse">Private Warehouse</option>
                        </select>
                      </div>

                      {/* Per-Location Modules */}
                      <div>
                        <div className="mb-2">
                          <h4 className="text-sm font-medium text-foreground">Modules</h4>
                          <p className="mt-0.5 text-[11px] text-muted-foreground">
                            Toggling here grants/revokes the business's ability to use a module at this location. When
                            you turn it off, the business sees the module as locked and can't enable it.
                          </p>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          {locationModules.map((mod) => {
                            const info = MODULE_INFO[mod.moduleName];
                            if (!info) return null;
                            const Icon = info.icon;
                            return (
                              <div key={mod.moduleName} className="rounded-lg border border-border bg-muted/30 p-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex min-w-0 items-center gap-2">
                                    <Icon className="h-4 w-4 text-primary" />
                                    <div className="min-w-0">
                                      <div className="text-sm font-medium">{info.name}</div>
                                      <div className="text-[11px] text-muted-foreground">
                                        {mod.masterGated && !mod.masterOpen ? (
                                          // Master switch is off — the location switch is moot
                                          // until the business-wide one above is turned on.
                                          <span>Turn on {info.name} for the business above to enable it here</span>
                                        ) : (SINGLE_PHASE_MODULES as readonly string[]).includes(mod.moduleName) ? (
                                          // Single-phase module — there's no merchant isActive
                                          // step, so the gate alone determines visibility.
                                          mod.superAdminEnabled ? (
                                            <span className="text-green-600">Enabled for this location</span>
                                          ) : (
                                            <span>Not enabled for this location</span>
                                          )
                                        ) : mod.superAdminEnabled ? (
                                          mod.isActive ? (
                                            <span className="text-green-600">
                                              Granted · business has the module turned on
                                            </span>
                                          ) : (
                                            <span>Granted · business has not turned the module on yet</span>
                                          )
                                        ) : mod.isActive ? (
                                          // Business has requested this module via the
                                          // onboarding/registration flow but the gate is
                                          // still closed. Highlights what to grant when
                                          // reviewing a pending tenant.
                                          <span className="font-medium text-amber-700">
                                            Business has requested this module · awaiting your grant
                                          </span>
                                        ) : (
                                          <span className="text-muted-foreground">
                                            Not granted · business has not requested this module
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    title={
                                      mod.superAdminEnabled
                                        ? `Disable ${info.name} for this location (forces it off for the business)`
                                        : `Allow the business to enable ${info.name} at this location`
                                    }
                                    onClick={() =>
                                      toggleModule.mutate({
                                        tenantId,
                                        moduleName: mod.moduleName,
                                        isActive: !mod.superAdminEnabled,
                                        locationId: loc.id,
                                      })
                                    }
                                    disabled={toggleModule.isPending || (mod.masterGated && !mod.masterOpen)}
                                    className={cn(
                                      'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                                      mod.superAdminEnabled ? 'bg-primary' : 'bg-gray-200',
                                    )}
                                  >
                                    <span
                                      className={cn(
                                        'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                                        mod.superAdminEnabled ? 'translate-x-4' : 'translate-x-0.5',
                                      )}
                                    />
                                  </button>
                                </div>
                                {/* Receive Payments carries two sub-switches for the
                                    payment methods the merchant may take on New Sale.
                                    Only meaningful while the module gate is open. */}
                                {mod.moduleName === 'collections' && mod.superAdminEnabled && (
                                  <>
                                    <ReceivePaymentMethodSwitches tenantId={tenantId} location={loc} />
                                    <PaymentsApiRoutingSwitch
                                      tenantId={tenantId}
                                      merchantId={(loc as any).externalId}
                                      direction="RECEIVE"
                                    />
                                  </>
                                )}
                                {/* Payouts route independently of receives, so the
                                    Send Payments gate carries its own switch. */}
                                {mod.moduleName === 'payables' && mod.superAdminEnabled && (
                                  <PaymentsApiRoutingSwitch
                                    tenantId={tenantId}
                                    merchantId={(loc as any).externalId}
                                    direction="SEND"
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Per-Location Customer Engagement gate. Super-admin only.
                          Controls whether the Customer Engagement tab is visible
                          inside POS Management for this location. Stored on
                          products.location_pos_config.customer_engagement_enabled.
                          Default: OFF. tenantId is threaded through so the
                          products service request carries X-Tenant-Id and clears
                          the products-side tenant guard for cross-tenant ops. */}
                      {/* Everything from here to the Invoice Processor gate is
                          POS surface area — tabs inside POS Management, the POS
                          app's theme, campaign senders for its customers. A
                          shelf-labels-only business has no till for any of it to
                          appear in. */}
                      {!isEslOnly && <CustomerEngagementToggleRow locationId={loc.id} tenantId={tenantId} />}

                      {/* Per-Location SMS Campaign sender pool. Super-admin
                          only. The pos-backend campaign worker round-robins
                          recipients across these numbers via Telnyx. Empty =
                          no campaign sender; scheduling a campaign here is
                          rejected with a contact-support error. Distinct from
                          the transactional sender on locations.sms_phone_number. */}
                      {!isEslOnly && <CampaignSmsNumbersRow locationId={loc.id} tenantId={tenantId} />}

                      {/* Per-Location Campaign API credentials (design v0.3 §11).
                          Super-admin only, behind the Campaign API platform
                          switch. Sends use this location's Customer Management
                          switch and the campaign senders just above. */}
                      {!isEslOnly && campaignApiPlatformEnabled && (
                        <CampaignApiCredentialsRow
                          tenantId={tenantId}
                          locationId={loc.id}
                          timeZone={loc.timezone || DEFAULT_TIME_ZONE}
                        />
                      )}

                      {/* Per-Location Promotions gate. Super-admin only.
                          Controls whether the Promotions tab inside POS
                          Management AND the Promotions sub-tab inside
                          Reports → POS are visible for this location. Stored
                          on products.location_pos_config.promotions_enabled.
                          Default: OFF. Mirrors the Customer Engagement gate. */}
                      {!isEslOnly && <PromotionsToggleRow locationId={loc.id} tenantId={tenantId} />}

                      {/* Per-Location Brand Colors gate. Super-admin only.
                          Controls whether the merchant can edit Store Config →
                          Brand Colors AND whether saved colors are pushed to
                          the POS app. Stored on
                          products.location_pos_config.brand_colors_enabled.
                          Default: ON (inverse of the other two gates) because
                          brand colors are an already-shipped surface — every
                          existing location is currently allowed to edit them. */}
                      {!isEslOnly && <BrandColorsToggleRow locationId={loc.id} tenantId={tenantId} />}

                      {/* Per-Location Invoice Processor gate. Super-admin only —
                          no merchant-side toggle exists, so this is the only
                          on/off control. Grouped next to Customer Engagement
                          because both follow the same super-admin-only model. */}
                      <InvoiceProcessorToggleRow
                        tenantId={tenantId}
                        locationId={loc.id}
                        superAdminEnabled={
                          (loc.modules as Array<any> | undefined)?.find(
                            (m: any) => m.moduleName === 'invoice_processor',
                          )?.superAdminEnabled === true
                        }
                      />

                      {/* Per-Location product data wipe (super-admin only).
                          Clears pricing/units/assignment/inventory + per-location
                          field-override locks for this location so it can be
                          re-imported. Base catalog stays tenant-wide. */}
                      <WipeLocationProductsRow locationId={loc.id} locationName={loc.name} tenantId={tenantId} />

                      {/* Per-Location Payments Management */}
                      {(() => {
                        // Use superAdminEnabled (the gate) rather than isActive
                        // (business-admin toggle) so the super admin can configure
                        // gateways as soon as they enable the module — before the
                        // business admin has activated it on their side.
                        const receiveEnabled =
                          locationModules.find((m) => m.moduleName === 'collections')?.superAdminEnabled ?? false;
                        const sendEnabled =
                          locationModules.find((m) => m.moduleName === 'payables')?.superAdminEnabled ?? false;
                        return receiveEnabled || sendEnabled ? (
                          <RpPaymentGatewaysConfigCard
                            tenantId={tenantId}
                            merchantId={(loc as any).externalId}
                            receiveEnabled={receiveEnabled}
                            sendEnabled={sendEnabled}
                          />
                        ) : null;
                      })()}

                      {/* Per-Location Funding Accounts (super-admin can add/fix when onboarding bank details are missing or mismatched) */}
                      <FundingAccountsCard
                        tenantId={tenantId}
                        locationId={loc.id}
                        onboardingBank={
                          loc.onboarding?.bankName
                            ? {
                                bankName: loc.onboarding.bankName,
                                routingNumber: loc.onboarding.routingNumber,
                                accountNumber: loc.onboarding.accountNumber,
                              }
                            : null
                        }
                      />

                      {/* Registration / Onboarding Info */}
                      {loc.onboarding?.isSubmitted ? (
                        <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-4">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <h4 className="text-sm font-semibold text-foreground">Registration Application</h4>
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
                              Submitted
                              {loc.onboarding.submittedAt ? ` on ${formatDateTime(loc.onboarding.submittedAt)}` : ''}
                            </span>
                          </div>

                          {/* Business Type */}
                          <div>
                            <h5 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              Business Type
                            </h5>
                            <p className="text-sm text-foreground">
                              {loc.onboarding.businessType === 'company'
                                ? 'Company'
                                : loc.onboarding.businessType === 'individual'
                                  ? 'Individual'
                                  : loc.onboarding.businessType === 'non_profit'
                                    ? 'Non-profit'
                                    : loc.onboarding.businessType === 'warehouse'
                                      ? 'Warehouse'
                                      : 'Not specified'}
                            </p>
                          </div>

                          {/* Business Details */}
                          <div>
                            <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              Business Details
                            </h5>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                              <div>
                                <p className="text-xs text-muted-foreground">Legal Name</p>
                                <p className="text-sm text-foreground">{loc.onboarding.legalBusinessName || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">EIN</p>
                                <p className="text-sm text-foreground">{loc.onboarding.ein || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Industry</p>
                                <p className="text-sm text-foreground">{loc.onboarding.industry || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Website</p>
                                <p className="text-sm text-foreground">{loc.onboarding.businessWebsite || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Phone</p>
                                <p className="text-sm text-foreground">{loc.onboarding.businessPhone || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Address</p>
                                <p className="text-sm text-foreground">
                                  {[
                                    loc.onboarding.businessStreet,
                                    loc.onboarding.businessCity,
                                    getStateName(loc.onboarding.businessState),
                                    loc.onboarding.businessZip,
                                  ]
                                    .filter(Boolean)
                                    .join(', ') || 'N/A'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Country</p>
                                <p className="text-sm text-foreground">{loc.onboarding.businessCountry || 'N/A'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Representative */}
                          <div>
                            <div className="mb-2 flex items-center gap-1.5">
                              <User className="h-3.5 w-3.5 text-muted-foreground" />
                              <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                Business Representative
                              </h5>
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                              <div>
                                <p className="text-xs text-muted-foreground">Name</p>
                                <p className="text-sm text-foreground">
                                  {[loc.onboarding.repFirstName, loc.onboarding.repLastName]
                                    .filter(Boolean)
                                    .join(' ') || 'N/A'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Email</p>
                                <p className="text-sm text-foreground">{loc.onboarding.repEmail || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Phone</p>
                                <p className="text-sm text-foreground">{loc.onboarding.repPhone || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Job Title</p>
                                <p className="text-sm text-foreground">{loc.onboarding.repJobTitle || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Ownership %</p>
                                <p className="text-sm text-foreground">
                                  {loc.onboarding.ownershipPercentage
                                    ? `${loc.onboarding.ownershipPercentage}%`
                                    : 'N/A'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">DOB</p>
                                <p className="text-sm text-foreground">{loc.onboarding.repDob || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">SSN</p>
                                {/* TODO(audit): unmasked SSN display for super-admins is an interim posture.
                                    Replace with reveal-on-click + audit log row (admin_user_id, tenant_id,
                                    location_id, viewed_at, reason) before broader access expands. */}
                                <p className="text-sm text-foreground">{loc.onboarding.repSsnLast4 || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Address</p>
                                <p className="text-sm text-foreground">
                                  {[
                                    loc.onboarding.repStreet,
                                    loc.onboarding.repCity,
                                    getStateName(loc.onboarding.repState),
                                    loc.onboarding.repZip,
                                  ]
                                    .filter(Boolean)
                                    .join(', ') || 'N/A'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Country</p>
                                <p className="text-sm text-foreground">{loc.onboarding.repCountry || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">DL Number</p>
                                <p className="text-sm text-foreground">
                                  {loc.onboarding.repDriverLicenseNumber || 'N/A'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">DL State</p>
                                <p className="text-sm text-foreground">
                                  {loc.onboarding.repDriverLicenseState
                                    ? getStateName(loc.onboarding.repDriverLicenseState)
                                    : 'N/A'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">DL Expiry</p>
                                <p className="text-sm text-foreground">
                                  {loc.onboarding.repDriverLicenseExpiry || 'N/A'}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Banking Details */}
                          {(loc.onboarding.bankName ||
                            loc.onboarding.routingNumber ||
                            loc.onboarding.accountNumber) && (
                            <div>
                              <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                Banking Details
                              </h5>
                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                <div>
                                  <p className="text-xs text-muted-foreground">Bank Name</p>
                                  <p className="text-sm text-foreground">{loc.onboarding.bankName || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Bank Representative</p>
                                  <p className="text-sm text-foreground">
                                    {loc.onboarding.bankRepresentativeName || 'N/A'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Routing Number</p>
                                  <p className="text-sm text-foreground">{loc.onboarding.routingNumber || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Account Number</p>
                                  <p className="font-mono text-sm text-foreground">
                                    {loc.onboarding.accountNumber || 'N/A'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Registration: </span>
                          <span className="text-amber-600">Not submitted</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Private warehouses — cross-tenant store ↔ warehouse mappings */}
      {tenant.type !== 'esl' && tenant.type !== 'companion' && tenant.locations && tenant.locations.length > 0 && (
        <PrivateWarehouseMappingsCard tenantId={tenantId} locations={tenant.locations} />
      )}

      {/* Business Branding */}
      <BusinessBrandingCard tenantId={tenantId} />

      {/* Deactivate Confirmation Dialog */}
      <ConfirmDialog
        open={showDeactivateConfirm}
        title="Deactivate Business"
        message={`Are you sure you want to deactivate "${tenant.name}"? This business and its users will lose access until reactivated.`}
        confirmLabel="Deactivate"
        cancelLabel="Cancel"
        variant="danger"
        loading={deactivateTenant.isPending}
        onConfirm={() => {
          deactivateTenant.mutate(tenantId, {
            onSuccess: () => setShowDeactivateConfirm(false),
          });
        }}
        onCancel={() => setShowDeactivateConfirm(false)}
      />

      {/* Registration / Onboarding Info */}
      {tenant.onboarding && (
        <div className="rounded-xl border border-border bg-white p-6">
          <div className="mb-4 flex items-center gap-3">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold text-foreground">Registration Application</h2>
            <StatusBadge status={tenant.onboarding.isSubmitted ? 'active' : 'inactive'} />
          </div>

          <div className="space-y-4">
            {/* Business Type */}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Business Type</h3>
              <p className="text-sm text-muted-foreground">
                {tenant.onboarding.businessType === 'company'
                  ? 'Company'
                  : tenant.onboarding.businessType === 'individual'
                    ? 'Individual'
                    : tenant.onboarding.businessType === 'non_profit'
                      ? 'Non-profit'
                      : tenant.onboarding.businessType === 'warehouse'
                        ? 'Warehouse'
                        : 'Not specified'}
              </p>
            </div>

            {/* Business Details */}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Business Details</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <p className="text-xs text-muted-foreground">Legal Name</p>
                  <p className="text-sm text-foreground">{tenant.onboarding.legalBusinessName || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">EIN</p>
                  <p className="text-sm text-foreground">{tenant.onboarding.ein || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Industry</p>
                  <p className="text-sm text-foreground">
                    {tenant.onboarding.industry === 'Others' && tenant.onboarding.otherIndustry
                      ? `Others – ${tenant.onboarding.otherIndustry}`
                      : tenant.onboarding.industry || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Website</p>
                  <p className="text-sm text-foreground">{tenant.onboarding.businessWebsite || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="text-sm text-foreground">{formatPhone(tenant.onboarding.businessPhone) || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Address</p>
                  <p className="text-sm text-foreground">
                    {[
                      tenant.onboarding.businessStreet,
                      tenant.onboarding.businessCity,
                      getStateName(tenant.onboarding.businessState),
                      tenant.onboarding.businessZip,
                    ]
                      .filter(Boolean)
                      .join(', ') || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Country</p>
                  <p className="text-sm text-foreground">{tenant.onboarding.businessCountry || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Representative */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">Business Representative</h3>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <p className="text-xs text-muted-foreground">Name</p>
                  <p className="text-sm text-foreground">
                    {[tenant.onboarding.repFirstName, tenant.onboarding.repLastName].filter(Boolean).join(' ') || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="text-sm text-foreground">{tenant.onboarding.repEmail || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="text-sm text-foreground">{formatPhone(tenant.onboarding.repPhone) || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Job Title</p>
                  <p className="text-sm text-foreground">{tenant.onboarding.repJobTitle || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Ownership Percentage</p>
                  <p className="text-sm text-foreground">
                    {tenant.onboarding.ownershipPercentage ? `${tenant.onboarding.ownershipPercentage}%` : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">DOB</p>
                  <p className="text-sm text-foreground">{tenant.onboarding.repDob || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">SSN</p>
                  {/* TODO(audit): unmasked SSN display for super-admins is an interim posture.
                      Replace with reveal-on-click + audit log row (admin_user_id, tenant_id,
                      viewed_at, reason) before broader access expands. */}
                  <p className="text-sm text-foreground">{tenant.onboarding.repSsnLast4 || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Address</p>
                  <p className="text-sm text-foreground">
                    {[
                      tenant.onboarding.repStreet,
                      tenant.onboarding.repCity,
                      getStateName(tenant.onboarding.repState),
                      tenant.onboarding.repZip,
                    ]
                      .filter(Boolean)
                      .join(', ') || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Country</p>
                  <p className="text-sm text-foreground">{tenant.onboarding.repCountry || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">DL Number</p>
                  <p className="text-sm text-foreground">{tenant.onboarding.repDriverLicenseNumber || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">DL State</p>
                  <p className="text-sm text-foreground">
                    {tenant.onboarding.repDriverLicenseState
                      ? getStateName(tenant.onboarding.repDriverLicenseState)
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">DL Expiry</p>
                  <p className="text-sm text-foreground">{tenant.onboarding.repDriverLicenseExpiry || 'N/A'}</p>
                </div>
                {tenant.onboarding.submittedAt && (
                  <div>
                    <p className="text-xs text-muted-foreground">Submitted</p>
                    <p className="text-sm text-foreground">{formatDateTime(tenant.onboarding.submittedAt)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Rp Payment Gateways Config Card ──
const RP_RECEIVE_CARD_OPTIONS = ['Clover', 'Stripe'];
const RP_RECEIVE_ACH_OPTIONS = ['Clover', 'iStream', 'Dwolla', 'Chase Batch'];
const RP_SEND_CARD_OPTIONS = ['Fiserv', 'Stripe'];
const RP_SEND_ACH_OPTIONS = ['iStream', 'Dwolla', 'Clover', 'Chase Batch'];

type GatewayRowType = 'RECEIVE_CARD' | 'RECEIVE_ACH' | 'SEND_CARD' | 'SEND_ACH';

const GATEWAY_ROW_META: Record<
  GatewayRowType,
  { label: string; type: 'SEND' | 'RECEIVE'; paymentMethod: string; options: string[] }
> = {
  RECEIVE_CARD: {
    label: 'Credit / Debit Cards Gateway',
    type: 'RECEIVE',
    paymentMethod: 'Credit / Debit Cards',
    options: RP_RECEIVE_CARD_OPTIONS,
  },
  RECEIVE_ACH: {
    label: 'ACH / Bank Gateway',
    type: 'RECEIVE',
    paymentMethod: 'ACH / Bank',
    options: RP_RECEIVE_ACH_OPTIONS,
  },
  SEND_CARD: {
    label: 'Credit / Debit Cards Gateway',
    type: 'SEND',
    paymentMethod: 'Credit / Debit Cards',
    options: RP_SEND_CARD_OPTIONS,
  },
  SEND_ACH: {
    label: 'ACH / Bank Gateway',
    type: 'SEND',
    paymentMethod: 'ACH / Bank',
    options: RP_SEND_ACH_OPTIONS,
  },
};

function GatewayConfigRow({
  tenantId,
  merchantId,
  rowKey,
  activeConfig,
}: {
  tenantId: string;
  merchantId: string;
  rowKey: GatewayRowType;
  activeConfig?: {
    paymentsApi: string;
    accessKey: string | null;
    secretKey: string | null;
    usePaymentsApi?: boolean;
  };
}) {
  const meta = GATEWAY_ROW_META[rowKey];
  const saveConfig = useSaveRpPaymentGatewayConfig();
  const [gateway, setGateway] = useState(activeConfig?.paymentsApi || '');
  const [accessKey, setAccessKey] = useState(activeConfig?.accessKey || '');
  const [secretKey, setSecretKey] = useState(activeConfig?.secretKey || '');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  // iStream token generator state
  const [showTokenGen, setShowTokenGen] = useState(false);
  const [tokenUsername, setTokenUsername] = useState('');
  const [tokenPassword, setTokenPassword] = useState('');
  const [tokenGenError, setTokenGenError] = useState('');

  const handleGenerateToken = () => {
    if (!tokenUsername.trim() || !tokenPassword.trim()) {
      setTokenGenError('Both username and password are required');
      return;
    }
    const token = btoa(`${tokenUsername.trim()}:${tokenPassword.trim()}`);
    setSecretKey(token);
    setShowTokenGen(false);
    setTokenUsername('');
    setTokenPassword('');
    setTokenGenError('');
    setShowSecret(true);
  };

  const handleSave = async () => {
    if (!gateway) {
      setError('Please select a gateway');
      return;
    }
    setError('');
    try {
      await saveConfig.mutateAsync({
        tenantId,
        merchantId,
        type: meta.type,
        paymentMethod: meta.paymentMethod,
        paymentsApi: gateway,
        accessKey: accessKey || undefined,
        secretKey: secretKey || undefined,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      toast.success('Configuration saved successfully');
    } catch {
      toast.error('Failed to save configuration');
    }
  };

  return (
    <div className="rounded-lg border border-border bg-muted/10 p-4 space-y-3">
      {/* Gateway label + active badge */}
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">
          {meta.label} <span className="text-red-500">*</span>
        </label>
        {activeConfig && (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
            <CheckCircle2 className="h-3 w-3" /> Active: {activeConfig.paymentsApi}
          </span>
        )}
      </div>

      {/* Dropdown */}
      <select
        value={gateway}
        onChange={(e) => {
          setGateway(e.target.value);
          setError('');
        }}
        className={cn(
          'h-9 w-full rounded-lg border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary',
          error ? 'border-red-500' : 'border-input',
        )}
      >
        <option value="">Select Gateway</option>
        {meta.options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      {error && <p className="text-xs text-red-500">{error}</p>}

      {/* Keys */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground">
            <Key className="h-3 w-3" /> Access Key
          </label>
          <input
            type="text"
            value={accessKey}
            onChange={(e) => setAccessKey(e.target.value)}
            placeholder="Enter access key"
            className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        <div>
          <label className="mb-1 flex items-center justify-between gap-1 text-xs font-medium text-muted-foreground">
            <span className="flex items-center gap-1">
              <Key className="h-3 w-3" /> Secret Key
            </span>
            {gateway.toLowerCase() === 'istream' && (
              <button
                type="button"
                onClick={() => {
                  setShowTokenGen((prev) => !prev);
                  setTokenGenError('');
                }}
                className="text-[10px] font-medium text-primary hover:underline"
              >
                {showTokenGen ? 'Cancel' : 'Generate iStream Token'}
              </button>
            )}
          </label>

          {/* iStream token generator — inline utility */}
          {showTokenGen && gateway.toLowerCase() === 'istream' && (
            <div className="mb-2 rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2">
              <p className="text-[10px] text-muted-foreground">
                Enter your iStream API credentials to generate token (
                <code className="text-[10px]">username:password</code>).
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  type="text"
                  value={tokenUsername}
                  onChange={(e) => setTokenUsername(e.target.value)}
                  placeholder="API Username"
                  className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
                <input
                  type="password"
                  value={tokenPassword}
                  onChange={(e) => setTokenPassword(e.target.value)}
                  placeholder="API Password"
                  className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  onKeyDown={(e) => e.key === 'Enter' && handleGenerateToken()}
                />
              </div>
              {tokenGenError && <p className="text-[10px] text-red-500">{tokenGenError}</p>}
              <button
                type="button"
                onClick={handleGenerateToken}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1 text-[11px] font-medium text-primary-foreground hover:bg-primary/90"
              >
                <Key className="h-3 w-3" /> Generate & Fill
              </button>
            </div>
          )}

          <div className="relative">
            <input
              type={showSecret ? 'text' : 'password'}
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              placeholder={
                gateway.toLowerCase() === 'istream' ? 'Base64 api_token (or use Generate above)' : 'Enter secret key'
              }
              className="h-9 w-full rounded-lg border border-input bg-background px-3 pr-9 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <button
              type="button"
              onClick={() => setShowSecret((prev) => !prev)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              tabIndex={-1}
            >
              {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saveConfig.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {saveConfig.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {saved ? '✓ Saved' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function RpPaymentGatewaysConfigCard({
  tenantId,
  merchantId,
  receiveEnabled = true,
  sendEnabled = true,
}: {
  tenantId: string;
  merchantId: string;
  receiveEnabled?: boolean;
  sendEnabled?: boolean;
}) {
  const { data, isLoading } = useRpPaymentGatewaysConfig(tenantId, merchantId);
  const configs = data?.data || [];

  const getActiveConfig = (type: 'SEND' | 'RECEIVE', paymentMethod: string) =>
    configs.find((c) => c.type === type && c.paymentMethod === paymentMethod) || undefined;

  if (isLoading) {
    return (
      <div className="mt-4 flex h-16 items-center justify-center text-sm text-muted-foreground">
        Loading payment configuration...
      </div>
    );
  }

  if (!merchantId) {
    return (
      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
        Merchant ID (external ID) not available for this location.
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center gap-2">
        <Settings2 className="h-4 w-4 text-muted-foreground" />
        <h4 className="text-sm font-medium text-foreground">Payments Management</h4>
        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">MID: {merchantId}</span>
      </div>

      {/* ── Receive Payments Section ── */}
      {receiveEnabled && (
        <div className="space-y-3">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Receive Payments</h5>
          <GatewayConfigRow
            tenantId={tenantId}
            merchantId={merchantId}
            rowKey="RECEIVE_CARD"
            activeConfig={getActiveConfig('RECEIVE', 'Credit / Debit Cards')}
          />
          <GatewayConfigRow
            tenantId={tenantId}
            merchantId={merchantId}
            rowKey="RECEIVE_ACH"
            activeConfig={getActiveConfig('RECEIVE', 'ACH / Bank')}
          />
        </div>
      )}

      {/* ── Send Payments Section ── */}
      {sendEnabled && (
        <div className="space-y-3">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Send Payments</h5>
          <GatewayConfigRow
            tenantId={tenantId}
            merchantId={merchantId}
            rowKey="SEND_CARD"
            activeConfig={getActiveConfig('SEND', 'Credit / Debit Cards')}
          />
          <GatewayConfigRow
            tenantId={tenantId}
            merchantId={merchantId}
            rowKey="SEND_ACH"
            activeConfig={getActiveConfig('SEND', 'ACH / Bank')}
          />
        </div>
      )}
    </div>
  );
}

// ── Business Branding Card ──
function BusinessBrandingCard({ tenantId }: { tenantId: string }) {
  const { data, isLoading } = useBusinessBranding(tenantId);
  const saveBranding = useSaveBusinessBranding();
  const uploadLogo = useUploadLogo();
  const uploadFavicon = useUploadFavicon();

  const [businessTitle, setBusinessTitle] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState('');
  const [cropType, setCropType] = useState<'logo' | 'favicon'>('logo');

  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  const branding = data?.data;

  // Pre-fill branding data when loaded (without useEffect to avoid eslint set-state-in-effect)
  if (branding && !initialized) {
    setBusinessTitle(branding.businessTitle || '');
    setBusinessDescription(branding.businessDescription || '');
    setLogoPreview(branding.logoUrl || null);
    setFaviconPreview(branding.faviconUrl || null);
    setInitialized(true);
  }
  if (!isLoading && !branding && !initialized) {
    setInitialized(true);
  }

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast.error('Invalid file type. Accepted: PNG, JPG, SVG');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5 MB');
      return;
    }

    setCropType('logo');
    setCropImageSrc(URL.createObjectURL(file));
    setCropModalOpen(true);
    e.target.value = '';
  };

  const handleFaviconSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'image/png') {
      toast.error('Favicon must be PNG');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Max 2 MB');
      return;
    }

    setCropType('favicon');
    setCropImageSrc(URL.createObjectURL(file));
    setCropModalOpen(true);
    e.target.value = '';
  };

  const handleCropConfirm = async (croppedBlob: Blob) => {
    setCropModalOpen(false);
    const file = new File([croppedBlob], cropType === 'logo' ? 'logo.png' : 'favicon.png', {
      type: 'image/png',
    });
    const previewUrl = URL.createObjectURL(croppedBlob);

    if (cropType === 'logo') {
      setLogoPreview(previewUrl);
      try {
        await uploadLogo.mutateAsync({ tenantId, file });
        toast.success('Logo uploaded successfully');
      } catch (err: unknown) {
        const msg = (err as { error?: { message?: string } })?.error?.message || 'Failed to upload logo';
        toast.error(msg);
        setLogoPreview(branding?.logoUrl || null);
      }
    } else {
      setFaviconPreview(previewUrl);
      try {
        await uploadFavicon.mutateAsync({ tenantId, file });
        toast.success('Favicon uploaded successfully');
      } catch (err: unknown) {
        const msg = (err as { error?: { message?: string } })?.error?.message || 'Failed to upload favicon';
        toast.error(msg);
        setFaviconPreview(branding?.faviconUrl || null);
      }
    }
  };

  const handleSave = async () => {
    try {
      await saveBranding.mutateAsync({ tenantId, businessTitle, businessDescription });
      toast.success('Branding saved successfully');
    } catch {
      toast.error('Failed to save branding');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center rounded-xl border border-border bg-white">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <div className="mb-5 flex items-center gap-3">
        <Palette className="h-5 w-5 text-muted-foreground" />
        <div>
          <h2 className="text-lg font-semibold text-foreground">Business Branding</h2>
          <p className="text-sm text-muted-foreground">
            Customize logo, favicon, and business name for white-label experience.
          </p>
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        {/* Left Column: Logo + Favicon uploads */}
        <div className="space-y-4">
          {/* Logo Upload */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Logo</label>
            <p className="mb-2 text-xs text-muted-foreground">
              Accepted: PNG, JPG, SVG. Max 5 MB. Recommended: 360×72px (horizontal) or 256×256px (square).
            </p>
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-dashed border-input bg-muted/30">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo preview" className="h-full w-full object-contain p-1" />
                ) : (
                  <ImageIcon className="h-8 w-8 text-muted-foreground/50" />
                )}
              </div>
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadLogo.isPending}
                className="inline-flex items-center gap-2 rounded-lg border border-input px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
              >
                {uploadLogo.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploadLogo.isPending ? 'Uploading...' : 'Upload Logo'}
              </button>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                className="hidden"
                onChange={handleLogoSelect}
              />
            </div>
          </div>

          {/* Favicon Upload */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Favicon</label>
            <p className="mb-2 text-xs text-muted-foreground">PNG only. Recommended: 32x32 or 48x48.</p>
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-dashed border-input bg-muted/30">
                {faviconPreview ? (
                  <img src={faviconPreview} alt="Favicon preview" className="h-full w-full object-contain p-1" />
                ) : (
                  <ImageIcon className="h-5 w-5 text-muted-foreground/50" />
                )}
              </div>
              <button
                type="button"
                onClick={() => faviconInputRef.current?.click()}
                disabled={uploadFavicon.isPending}
                className="inline-flex items-center gap-2 rounded-lg border border-input px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
              >
                {uploadFavicon.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {uploadFavicon.isPending ? 'Uploading...' : 'Upload Favicon'}
              </button>
              <input
                ref={faviconInputRef}
                type="file"
                accept="image/png"
                className="hidden"
                onChange={handleFaviconSelect}
              />
            </div>
          </div>
        </div>

        {/* Right Column: Title + Description */}
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              Business Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={businessTitle}
              onChange={(e) => setBusinessTitle(e.target.value)}
              placeholder="Your Business Name"
              className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Description</label>
            <textarea
              value={businessDescription}
              onChange={(e) => setBusinessDescription(e.target.value)}
              placeholder="Optional tagline or description"
              rows={3}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary resize-none"
            />
          </div>
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saveBranding.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {saveBranding.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Save Branding
        </button>
      </div>

      {/* Image Crop Modal */}
      <ImageCropModal
        open={cropModalOpen}
        imageSrc={cropImageSrc}
        aspectRatio={cropType === 'favicon' ? 1 : undefined}
        title={cropType === 'logo' ? 'Crop Logo' : 'Crop Favicon'}
        description={
          cropType === 'logo'
            ? 'Crop your logo to fit the sidebar. Use 5:1 for horizontal or 1:1 for square logos.'
            : 'Crop your favicon to a square shape (32×32 or 48×48 recommended).'
        }
        onConfirm={handleCropConfirm}
        onCancel={() => setCropModalOpen(false)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-location Funding Accounts — super-admin view, with add/edit as a fallback
// for missing or mismatched onboarding bank details
// ─────────────────────────────────────────────────────────────────────────────

function last4(s: string | undefined | null): string {
  if (!s) return '—';
  return s.length <= 4 ? s : '••••' + s.slice(-4);
}

function formatRouting(s: string | undefined | null): string {
  return s ? s : '—';
}

function FundingAccountRow({
  account,
  onEdit,
  onSetPrimary,
}: {
  account: AdminFundingAccount;
  onEdit: () => void;
  onSetPrimary: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{account.bankName}</span>
          {account.isPrimary && (
            <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
              Primary
            </span>
          )}
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
            {account.accountType}
          </span>
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          Routing {formatRouting(account.routingNumber)} · Account {last4(account.accountNumber)} ·{' '}
          {account.accountHolderName}
        </div>
      </div>
      {!account.isPrimary && (
        <button
          type="button"
          onClick={onSetPrimary}
          title="Set as primary"
          aria-label={`Set ${account.bankName} as primary funding account`}
          className="ml-2 inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs text-amber-700 hover:bg-amber-50"
        >
          <Star className="h-3.5 w-3.5" />
          Set primary
        </button>
      )}
      <button
        type="button"
        onClick={onEdit}
        title="Fix bank details"
        aria-label={`Edit ${account.bankName} funding account`}
        className="ml-2 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function FundingAccountsCard({
  tenantId,
  locationId,
  onboardingBank,
}: {
  tenantId: string;
  locationId: string;
  onboardingBank: { bankName: string; routingNumber: string; accountNumber: string } | null;
}) {
  const { data, isLoading } = useAdminFundingAccounts(tenantId, locationId);
  const [modal, setModal] = useState<
    { mode: 'create' } | { mode: 'edit'; side: AdminFundingSide; account: AdminFundingAccount } | null
  >(null);
  const setPrimary = useAdminSetPrimaryFundingAccount(tenantId, locationId);
  const [primaryConfirm, setPrimaryConfirm] = useState<{ side: AdminFundingSide; account: AdminFundingAccount } | null>(
    null,
  );

  const handleConfirmPrimary = async () => {
    if (!primaryConfirm) return;
    try {
      await setPrimary.mutateAsync({ side: primaryConfirm.side, id: primaryConfirm.account.id });
      toast.success(`${primaryConfirm.account.nickname} is now the primary account`);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to set primary funding account'));
    }
    setPrimaryConfirm(null);
  };

  const sendAccounts = data?.data?.send ?? [];
  const receiveAccounts = data?.data?.receive ?? [];
  const sendError = data?.errors?.send;
  const receiveError = data?.errors?.receive;

  // Detect a backfill gap: onboarding has bank info for this location but neither
  // service has a matching (routing, account) pair. This is the actionable warning
  // for operators during the rollout.
  const hasBackfillGap = (() => {
    if (!onboardingBank?.routingNumber || !onboardingBank?.accountNumber) return false;
    const matchingSend = sendAccounts.some(
      (a) => a.routingNumber === onboardingBank.routingNumber && a.accountNumber === onboardingBank.accountNumber,
    );
    const matchingReceive = receiveAccounts.some(
      (a) => a.routingNumber === onboardingBank.routingNumber && a.accountNumber === onboardingBank.accountNumber,
    );
    return !(matchingSend && matchingReceive);
  })();

  // Detect send/receive drift: the two services' primary differ for the same location.
  const sendPrimary = sendAccounts.find((a) => a.isPrimary && a.isActive);
  const receivePrimary = receiveAccounts.find((a) => a.isPrimary && a.isActive);
  const hasDrift =
    sendPrimary &&
    receivePrimary &&
    (sendPrimary.routingNumber !== receivePrimary.routingNumber ||
      sendPrimary.accountNumber !== receivePrimary.accountNumber);

  // Super-admin fallback: offer "Add" only when a side has no account or the
  // onboarding bank details don't match — the normal path is onboarding.
  const missingSides = (['send', 'receive'] as const).filter((side) =>
    side === 'send' ? !sendError && sendAccounts.length === 0 : !receiveError && receiveAccounts.length === 0,
  );
  const canAdd = !isLoading && (missingSides.length > 0 || hasBackfillGap);

  return (
    <div className="mt-4 rounded-lg border border-border bg-muted/20 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-semibold text-foreground">Funding Accounts</h4>
        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">Location-scoped</span>
        {canAdd && (
          <button
            type="button"
            onClick={() => setModal({ mode: 'create' })}
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-primary/30 bg-white px-2 py-1 text-xs font-medium text-primary hover:bg-primary/5"
          >
            <Plus className="h-3.5 w-3.5" />
            Add funding account
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-xs text-muted-foreground">Loading funding accounts…</div>
      ) : (
        <>
          {hasBackfillGap && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              <strong>Backfill gap:</strong> the onboarding form captured bank details for this location (
              {onboardingBank?.bankName} · {last4(onboardingBank?.accountNumber)}) but no matching funding account
              exists in one or both services. Use <strong>Add funding account</strong>, or edit the existing account to
              correct its bank details.
            </div>
          )}
          {hasDrift && (
            <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
              <strong>Send/Receive drift:</strong> the primary send-side account does not match the primary receive-side
              account for this location. They should normally be the same bank.
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            {/* Send side */}
            <div className="space-y-2">
              <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Send payments</h5>
              {sendError ? (
                <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  Service error: {sendError}
                </div>
              ) : sendAccounts.length === 0 ? (
                <div className="text-xs italic text-muted-foreground">
                  No send-side funding account for this location
                </div>
              ) : (
                <div className="space-y-2">
                  {sendAccounts.map((a) => (
                    <FundingAccountRow
                      key={a.id}
                      account={a}
                      onEdit={() => setModal({ mode: 'edit', side: 'send', account: a })}
                      onSetPrimary={() => setPrimaryConfirm({ side: 'send', account: a })}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Receive side */}
            <div className="space-y-2">
              <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Receive payments</h5>
              {receiveError ? (
                <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  Service error: {receiveError}
                </div>
              ) : receiveAccounts.length === 0 ? (
                <div className="text-xs italic text-muted-foreground">
                  No receive-side funding account for this location
                </div>
              ) : (
                <div className="space-y-2">
                  {receiveAccounts.map((a) => (
                    <FundingAccountRow
                      key={a.id}
                      account={a}
                      onEdit={() => setModal({ mode: 'edit', side: 'receive', account: a })}
                      onSetPrimary={() => setPrimaryConfirm({ side: 'receive', account: a })}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {modal?.mode === 'create' && (
        <AdminFundingAccountModal
          mode="create"
          tenantId={tenantId}
          locationId={locationId}
          defaultSides={missingSides.length > 0 ? [...missingSides] : ['send', 'receive']}
          prefill={onboardingBank}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.mode === 'edit' && (
        <AdminFundingAccountModal
          mode="edit"
          tenantId={tenantId}
          locationId={locationId}
          side={modal.side}
          account={modal.account}
          onClose={() => setModal(null)}
        />
      )}

      <ConfirmDialog
        open={!!primaryConfirm}
        title="Set primary funding account"
        message={
          primaryConfirm?.side === 'send'
            ? `Make "${primaryConfirm.account.nickname}" (${primaryConfirm.account.bankName} ${last4(primaryConfirm.account.accountNumber)}) the primary send account? All new payments from this location will be sent from it.`
            : `Make "${primaryConfirm?.account.nickname}" (${primaryConfirm?.account.bankName} ${last4(primaryConfirm?.account.accountNumber)}) the primary receive account for this location?`
        }
        confirmLabel="Set primary"
        cancelLabel="Cancel"
        variant="warning"
        loading={setPrimary.isPending}
        onConfirm={handleConfirmPrimary}
        onCancel={() => setPrimaryConfirm(null)}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Customer Engagement per-location toggle (super-admin only)
//
// This control lives inside the AdminTenantDetailPage's expanded location
// block — the only place super_admin actually lands in the dashboard
// (AdminRouter vs TenantRouter, see app.tsx). When ON, the Customer
// Engagement tab becomes visible inside POS Management at the matching
// location for *all* roles. Default is OFF for every location.
//
// The toggle backs onto products.location_pos_config.customer_engagement_enabled
// and is gated server-side by requireMinRole("super_admin") on
// PUT /locations/:locationId/pos-config/customer-engagement.
// ───────────────────────────────────────────────────────────────────────────
function CustomerEngagementToggleRow({ locationId, tenantId }: { locationId: string; tenantId: string }) {
  const { data, isLoading } = useProductsPosConfig(locationId, tenantId);
  const updateCE = useUpdateCustomerEngagementEnabled(tenantId);
  const enabled = data?.customerEngagementEnabled === true;

  const onToggle = () => {
    const next = !enabled;
    updateCE.mutate(
      { locationId, enabled: next },
      {
        onSuccess: () => {
          toast.success(`Customer Management ${next ? 'enabled' : 'disabled'} for this location`);
        },
        onError: (err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Failed to update Customer Management';
          toast.error(msg);
        },
      },
    );
  };

  return (
    <div>
      <div className="mb-2">
        <h4 className="text-sm font-medium text-foreground">Customer Management</h4>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          When ON, the Customer Management tab appears inside POS Management at this location (Customers, Coupons,
          Reward Programs, Credit Accounts, Campaigns). Default OFF. Super admin only.
        </p>
      </div>
      <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3">
        <div className="flex min-w-0 items-center gap-2">
          <Heart className="h-4 w-4 text-primary" />
          <div className="min-w-0">
            <div className="text-sm font-medium">Customer Management Tab</div>
            <div className="text-[11px] text-muted-foreground">
              {isLoading ? (
                <span>Loading…</span>
              ) : enabled ? (
                <span className="text-green-600">Enabled · tab is visible at this location</span>
              ) : (
                <span>Disabled · tab is hidden at this location</span>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          title={
            enabled ? 'Disable Customer Management at this location' : 'Enable Customer Management at this location'
          }
          onClick={onToggle}
          disabled={isLoading || updateCE.isPending}
          className={cn(
            'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
            enabled ? 'bg-primary' : 'bg-gray-200',
            (isLoading || updateCE.isPending) && 'opacity-60',
          )}
        >
          <span
            className={cn(
              'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
              enabled ? 'translate-x-4' : 'translate-x-0.5',
            )}
          />
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Campaign SMS sender pool (super-admin only)
//
// Per-location pool of from-numbers pos-backend uses for outgoing SMS
// marketing campaigns. The campaign worker round-robins recipients across the
// pool via Telnyx (never the AWS transactional numbers). At least one number
// is required to schedule a campaign — an empty pool surfaces a contact-support
// error at schedule time. Distinct from the transactional sender on
// locations.sms_phone_number so campaign vs transactional traffic doesn't
// cross-pollute carrier reputation.
//
// Saved against products.location_pos_config.campaign_sms_numbers via
// PUT /locations/:id/pos-config/campaign-sms-numbers (super_admin only).
// Server normalizes E.164 / 10-digit input and dedups.
// ───────────────────────────────────────────────────────────────────────────

/** Normalize a US phone to E.164 for display/diffing; returns '' if invalid. */
function normalizeCampaignNumber(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (trimmed.startsWith('+') && digits.length >= 10) return `+${digits}`;
  return '';
}

function CampaignSmsNumbersRow({ locationId, tenantId }: { locationId: string; tenantId: string }) {
  const { data, isLoading } = useProductsPosConfig(locationId, tenantId);
  const saved = data?.campaignSmsNumbers ?? [];

  return (
    <div>
      <div className="mb-2">
        <h4 className="text-sm font-medium text-foreground">Campaign SMS Senders</h4>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          From-numbers pos-backend round-robins across for outgoing SMS marketing campaigns at this location, sent via
          Telnyx. Add one or more Telnyx 10DLC numbers. At least one is required — with none assigned, scheduling a
          campaign here is rejected and the operator is told to contact support. Distinct from the transactional sender.
          Super admin only.
        </p>
      </div>
      {/* Remount the editor whenever the saved pool changes so useState's
          initializer picks up the fresh value without a useEffect(setState). */}
      <CampaignSmsNumbersEditor
        key={saved.join(',') || '__empty__'}
        locationId={locationId}
        tenantId={tenantId}
        saved={saved}
        isLoading={isLoading}
      />
    </div>
  );
}

function CampaignSmsNumbersEditor({
  locationId,
  tenantId,
  saved,
  isLoading,
}: {
  locationId: string;
  tenantId: string;
  saved: string[];
  isLoading: boolean;
}) {
  const update = useUpdateCampaignSmsNumbers(tenantId);
  const [numbers, setNumbers] = useState<string[]>(saved);
  const [draft, setDraft] = useState('');

  const dirty = numbers.join(',') !== saved.join(',');

  const addDraft = () => {
    const norm = normalizeCampaignNumber(draft);
    if (!norm) {
      toast.error('Enter a valid US number (+1XXXXXXXXXX or 10 digits)');
      return;
    }
    if (numbers.includes(norm)) {
      toast.error(`${norm} is already in the pool`);
      return;
    }
    setNumbers([...numbers, norm]);
    setDraft('');
  };

  const removeAt = (idx: number) => setNumbers(numbers.filter((_, i) => i !== idx));

  const onSave = () =>
    update.mutate(
      { locationId, campaignSmsNumbers: numbers },
      {
        onSuccess: (resp) => {
          toast.success(
            resp.campaignSmsNumbers.length === 0
              ? 'Campaign sender pool cleared'
              : `Saved ${resp.campaignSmsNumbers.length} campaign sender${resp.campaignSmsNumbers.length === 1 ? '' : 's'}`,
          );
        },
        onError: (err: unknown) => {
          toast.error(err instanceof Error ? err.message : 'Failed to update campaign senders');
        },
      },
    );

  const busy = isLoading || update.isPending;

  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
      {/* Current pool chips */}
      {numbers.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {numbers.map((n, idx) => (
            <span
              key={n}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-2.5 py-1 text-xs font-medium text-foreground"
            >
              {idx === 0 && <span className="text-[10px] uppercase tracking-wide text-primary">primary</span>}
              {n}
              <button
                type="button"
                onClick={() => removeAt(idx)}
                disabled={busy}
                className="ml-0.5 text-muted-foreground hover:text-destructive disabled:opacity-50"
                title={`Remove ${n}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-amber-600">
          No campaign sender assigned — campaigns at this location cannot be scheduled until you add a number.
        </p>
      )}

      {/* Add row */}
      <div className="flex items-center gap-2">
        <input
          type="tel"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addDraft();
            }
          }}
          placeholder="Add Telnyx number · +1XXXXXXXXXX"
          disabled={busy}
          className="h-9 flex-1 rounded-md border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-60"
        />
        <button
          type="button"
          onClick={addDraft}
          disabled={busy || !draft.trim()}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
        >
          Add
        </button>
      </div>

      {/* Save / reset */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || busy}
          className={cn(
            'rounded-md px-3 py-1.5 text-xs font-medium',
            dirty && !busy ? 'bg-primary text-primary-foreground hover:opacity-90' : 'bg-muted text-muted-foreground',
          )}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
        {dirty && !update.isPending && (
          <button
            type="button"
            onClick={() => {
              setNumbers(saved);
              setDraft('');
            }}
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
          >
            Reset
          </button>
        )}
        <span className="ml-auto text-[11px] text-muted-foreground">
          {numbers.length} number{numbers.length === 1 ? '' : 's'} · round-robin
        </span>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Promotions per-location toggle (super-admin only)
//
// Direct mirror of CustomerEngagementToggleRow. When ON, two UI surfaces
// appear at this location:
//   1. POS Management → Promotions tab (the existing PromotionListPage)
//   2. Reports → POS → Promotions sub-tab (the Promotion Usage report)
//
// Backed by products.location_pos_config.promotions_enabled and gated
// server-side by requireMinRole("super_admin") on
// PUT /locations/:locationId/pos-config/promotions. Default OFF.
// ───────────────────────────────────────────────────────────────────────────
function PromotionsToggleRow({ locationId, tenantId }: { locationId: string; tenantId: string }) {
  const { data, isLoading } = useProductsPosConfig(locationId, tenantId);
  const updatePromos = useUpdatePromotionsEnabled(tenantId);
  const enabled = data?.promotionsEnabled === true;

  const onToggle = () => {
    const next = !enabled;
    updatePromos.mutate(
      { locationId, enabled: next },
      {
        onSuccess: () => {
          toast.success(`Promotions ${next ? 'enabled' : 'disabled'} for this location`);
        },
        onError: (err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Failed to update Promotions';
          toast.error(msg);
        },
      },
    );
  };

  return (
    <div>
      <div className="mb-2">
        <h4 className="text-sm font-medium text-foreground">Promotions</h4>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          When ON, the Promotions tab appears inside POS Management at this location AND the Promotions sub-tab appears
          inside Reports → POS. Default OFF. Super admin only.
        </p>
      </div>
      <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3">
        <div className="flex min-w-0 items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <div className="min-w-0">
            <div className="text-sm font-medium">Promotions Tab</div>
            <div className="text-[11px] text-muted-foreground">
              {isLoading ? (
                <span>Loading…</span>
              ) : enabled ? (
                <span className="text-green-600">Enabled · tab is visible at this location</span>
              ) : (
                <span>Disabled · tab is hidden at this location</span>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          title={enabled ? 'Disable Promotions at this location' : 'Enable Promotions at this location'}
          onClick={onToggle}
          disabled={isLoading || updatePromos.isPending}
          className={cn(
            'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
            enabled ? 'bg-primary' : 'bg-gray-200',
            (isLoading || updatePromos.isPending) && 'opacity-60',
          )}
        >
          <span
            className={cn(
              'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
              enabled ? 'translate-x-4' : 'translate-x-0.5',
            )}
          />
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Brand Colors per-location toggle (super-admin only)
//
// Mirrors PromotionsToggleRow in shape but inverts the default polarity. The
// other two toggles default OFF because they gate opt-in features; brand
// colors are already-shipped surface — every existing location is allowed
// to edit them today, so the default is ON and flipping OFF *revokes*
// access. When OFF:
//   1. The Brand Colors block disappears from the merchant Store Config tab.
//   2. The next /pos/tenant-config push nulls the color hexes so the POS
//      app falls back to its built-in default theme. (pos-backend
//      enforcement; the dashboard just stops showing the section.)
// Saved hexes stay in the DB so flipping back ON restores them.
// ───────────────────────────────────────────────────────────────────────────
function BrandColorsToggleRow({ locationId, tenantId }: { locationId: string; tenantId: string }) {
  const { data, isLoading } = useProductsPosConfig(locationId, tenantId);
  const updateBrand = useUpdateBrandColorsEnabled(tenantId);
  // Default to true while loading so the visual state matches the column
  // default and a half-second flash of "Disabled" doesn't appear on mount.
  const enabled = data ? data.brandColorsEnabled === true : true;

  const onToggle = () => {
    const next = !enabled;
    updateBrand.mutate(
      { locationId, enabled: next },
      {
        onSuccess: (resp) => {
          // Two writes happen in `useUpdateBrandColorsEnabled`: products
          // (the gate) and pos-backend (the propagation). The first is
          // authoritative for the dashboard; the second is best-effort.
          // Surface a softer warning when only the pos-backend push fell
          // through so the super admin knows POS registers may lag until
          // a retry / next push.
          if (resp.posPushOk === false) {
            toast.error(
              `Brand Colors ${next ? 'enabled' : 'disabled'} in dashboard, but pos-backend push failed — POS devices may not reflect this until the next sync.`,
            );
          } else {
            toast.success(`Brand Colors ${next ? 'enabled' : 'disabled'} for this location`);
          }
        },
        onError: (err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Failed to update Brand Colors';
          toast.error(msg);
        },
      },
    );
  };

  return (
    <div>
      <div className="mb-2">
        <h4 className="text-sm font-medium text-foreground">Brand Colors</h4>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          When ON, the merchant can edit Store Config → Brand Colors and the saved colors apply in the POS app. When
          OFF, the section is hidden on the dashboard and the POS app uses its default theme. Default ON. Super admin
          only.
        </p>
      </div>
      <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3">
        <div className="flex min-w-0 items-center gap-2">
          <Palette className="h-4 w-4 text-primary" />
          <div className="min-w-0">
            <div className="text-sm font-medium">Brand Colors Section</div>
            <div className="text-[11px] text-muted-foreground">
              {isLoading ? (
                <span>Loading…</span>
              ) : enabled ? (
                <span className="text-green-600">Enabled · merchant can edit; colors apply on POS</span>
              ) : (
                <span>Disabled · section hidden; POS uses default theme</span>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          title={enabled ? 'Disable Brand Colors at this location' : 'Enable Brand Colors at this location'}
          onClick={onToggle}
          disabled={isLoading || updateBrand.isPending}
          className={cn(
            'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
            enabled ? 'bg-primary' : 'bg-gray-200',
            (isLoading || updateBrand.isPending) && 'opacity-60',
          )}
        >
          <span
            className={cn(
              'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
              enabled ? 'translate-x-4' : 'translate-x-0.5',
            )}
          />
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Invoice Processor per-location toggle (super-admin only)
//
// Mirrors CustomerEngagementToggleRow: there is no merchant-side toggle for
// invoice_processor, so flipping this gate here directly turns the module
// on/off for the location (no separate `isActive` to wait on). Backed by
// the tenant_modules row's `super_admin_enabled` for this (tenant, location).
// ───────────────────────────────────────────────────────────────────────────
function InvoiceProcessorToggleRow({
  tenantId,
  locationId,
  superAdminEnabled,
}: {
  tenantId: string;
  locationId: string;
  superAdminEnabled: boolean;
}) {
  const toggleModule = useToggleTenantModule();

  const onToggle = () => {
    const next = !superAdminEnabled;
    toggleModule.mutate(
      { tenantId, moduleName: 'invoice_processor', isActive: next, locationId },
      {
        onSuccess: () => {
          toast.success(`Invoice Processor ${next ? 'enabled' : 'disabled'} for this location`);
        },
        onError: (err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Failed to update Invoice Processor';
          toast.error(msg);
        },
      },
    );
  };

  return (
    <div>
      <div className="mb-2">
        <h4 className="text-sm font-medium text-foreground">Invoice Processor</h4>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          AI-powered vendor invoice parsing and bill management. Super-admin only — businesses have no self-serve
          toggle, so enabling here directly turns it on.
        </p>
      </div>
      <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3">
        <div className="flex min-w-0 items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          <div className="min-w-0">
            <div className="text-sm font-medium">Invoice Processor Tab</div>
            <div className="text-[11px] text-muted-foreground">
              {superAdminEnabled ? (
                <span className="text-green-600">Enabled · tab is visible at this location</span>
              ) : (
                <span>Disabled · tab is hidden at this location</span>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          title={
            superAdminEnabled
              ? 'Disable Invoice Processor at this location'
              : 'Enable Invoice Processor at this location'
          }
          onClick={onToggle}
          disabled={toggleModule.isPending}
          className={cn(
            'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
            superAdminEnabled ? 'bg-primary' : 'bg-gray-200',
            toggleModule.isPending && 'opacity-60',
          )}
        >
          <span
            className={cn(
              'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
              superAdminEnabled ? 'translate-x-4' : 'translate-x-0.5',
            )}
          />
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Per-location product-data wipe (super-admin only)
//
// Deletes ONLY the location-specific layer for this location so it can be
// re-imported from scratch: per-location pricing, units, assignment/inventory,
// and per-location field-override locks. Base products, departments, categories
// and vendors are tenant-wide and are NOT touched.
//
// Runs as a client-driven batch: the hook loops POST /wipe-batch (1000
// products/call) until the server reports nothing left, matching the
// import-jobs design (no server-side worker; progress survives a redeploy).
// tenantId is threaded through so the products service request carries
// X-Tenant-Id and operates against the tenant being viewed.
// ───────────────────────────────────────────────────────────────────────────
function WipeLocationProductsRow({
  locationId,
  locationName,
  tenantId,
}: {
  locationId: string;
  locationName: string;
  tenantId: string;
}) {
  const { data, isLoading } = useLocationPerLocationCount(tenantId, locationId);
  const wipe = useWipeLocationProducts();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const total = data?.total ?? 0;
  const running = wipe.isPending;

  const onConfirm = () => {
    setConfirmOpen(false);
    setProgress({ done: 0, total });
    wipe.mutate(
      {
        tenantId,
        locationId,
        onProgress: (done, t) => setProgress({ done, total: t }),
      },
      {
        onSuccess: ({ cleared }) => {
          setProgress(null);
          toast.success(`Cleared product data for ${cleared.toLocaleString()} product(s) at ${locationName}`);
        },
        onError: (err: unknown) => {
          setProgress(null);
          const msg = err instanceof Error ? err.message : 'Failed to wipe location product data';
          toast.error(msg);
        },
      },
    );
  };

  const pct = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div>
      <div className="mb-2">
        <h4 className="text-sm font-medium text-red-600">Danger Zone · Wipe Product Data</h4>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Permanently deletes this location&apos;s product pricing, units, assignment/inventory and override locks so it
          can be re-imported from scratch. The shared catalog (products, departments, categories, vendors) is NOT
          affected. Super admin only.
        </p>
      </div>
      <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50/50 p-3">
        <div className="flex min-w-0 items-center gap-2">
          <Trash2 className="h-4 w-4 text-red-600" />
          <div className="min-w-0">
            <div className="text-sm font-medium">Location product data</div>
            <div className="text-[11px] text-muted-foreground">
              {running && progress ? (
                <span>
                  Deleting… {progress.done.toLocaleString()} / {progress.total.toLocaleString()} ({pct}%)
                </span>
              ) : isLoading ? (
                <span>Loading…</span>
              ) : total > 0 ? (
                <span>{total.toLocaleString()} product(s) have data at this location</span>
              ) : (
                <span className="text-green-600">No location-specific product data</span>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={isLoading || running || total === 0}
          className={cn(
            'rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100',
            (isLoading || running || total === 0) && 'opacity-60',
          )}
        >
          {running ? (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Wiping…
            </span>
          ) : (
            'Wipe'
          )}
        </button>
      </div>

      {running && progress && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-red-100">
          <div className="h-full rounded-full bg-red-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        variant="danger"
        title="Wipe location product data?"
        message={`This permanently deletes all location-specific product data (pricing, units, assignment/inventory, override locks) for "${locationName}". ${total.toLocaleString()} product(s) will be cleared. The shared catalog is not affected. This cannot be undone.`}
        confirmLabel="Wipe data"
        onConfirm={onConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

/** Inline-editable location contact email: saves on blur / Enter when changed. */
function LocationContactEmailField({
  value,
  disabled,
  onSave,
}: {
  value: string;
  disabled?: boolean;
  onSave: (email: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const commit = () => {
    const next = draft.trim().toLowerCase();
    if (next !== (value ?? '').trim().toLowerCase()) onSave(next);
  };
  return (
    <input
      type="email"
      value={draft}
      disabled={disabled}
      placeholder="orders@store.com"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      className="h-9 w-56 rounded-lg border border-input px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50"
    />
  );
}
